import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { UPSTREAM_UNAVAILABLE_CODE, UPSTREAM_UNAVAILABLE_HEADER, UPSTREAM_UNAVAILABLE_KEY, upstreamUnavailableMessage } from '@raptor-fe/core';
import { ApplicationContextStore } from '../application-context/application-context.store';
import { ToastService } from '../toast-service/toast.service';

/**
 * Shows the store-offline toast, at most once per {@link UpstreamUnavailableNotifier.quietPeriodMs}. Screens poll
 * (My Work refreshes on a timer) and one page load can fire several GTX-backed requests, so without the quiet
 * period staff would get a stack of identical toasts for one outage.
 */
@Injectable({ providedIn: 'root' })
export class UpstreamUnavailableNotifier {
    /** Minimum gap between two store-offline toasts. */
    static readonly quietPeriodMs = 60_000;

    private readonly toastService = inject(ToastService);
    private readonly appContext = inject(ApplicationContextStore);
    private lastShownAt = Number.NEGATIVE_INFINITY;

    /**
     * Shows the toast in the tenant's wording unless one was shown within the quiet period, then has the toast
     * service drop screens' own copies of the same text for that period.
     *
     * @param serverMessage - The response's own message, when it has one; screens often toast it themselves.
     * @param now - Current time in milliseconds; injectable for tests.
     */
    notify(serverMessage?: string | null, now: number = Date.now()): void {
        if (now - this.lastShownAt < UpstreamUnavailableNotifier.quietPeriodMs) {
            return;
        }
        this.lastShownAt = now;

        const message =
            upstreamUnavailableMessage({ error: { code: UPSTREAM_UNAVAILABLE_CODE } }, this.appContext.dictionary()) ??
            UPSTREAM_UNAVAILABLE_KEY.label;
        void this.toastService.showError(message);
        this.toastService.markStoreOfflineShown([message, serverMessage, UPSTREAM_UNAVAILABLE_KEY.label], now);
    }
}

/**
 * Tells staff when the store's own system (GTX web service) is offline, from one place instead of per screen.
 * Reacts to an API error whose body or header carries {@link UPSTREAM_UNAVAILABLE_CODE}, and to a successful
 * response flagged with {@link UPSTREAM_UNAVAILABLE_HEADER} (last-known data served while the store is offline).
 * The response passes through unchanged, so screens keep their own handling.
 *
 * Sign-in requests are skipped: the login screens show the offline message themselves, inline on mobile and as
 * a toast on web, and a second toast would repeat it.
 */
export function upstreamUnavailableInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
    if (req.url.includes('/authentication/')) {
        return next(req);
    }

    const notifier = inject(UpstreamUnavailableNotifier);

    return next(req).pipe(
        tap({
            next: (event) => {
                if (event instanceof HttpResponse && event.headers.get(UPSTREAM_UNAVAILABLE_HEADER) === UPSTREAM_UNAVAILABLE_CODE) {
                    notifier.notify();
                }
            },
            error: (error: unknown) => {
                if (error instanceof HttpErrorResponse && isUpstreamUnavailable(error)) {
                    const serverMessage = (error.error as { message?: unknown } | null)?.message;
                    notifier.notify(typeof serverMessage === 'string' ? serverMessage : null);
                }
            },
        }),
    );
}

function isUpstreamUnavailable(error: HttpErrorResponse): boolean {
    const bodyCode = (error.error as { code?: unknown } | null)?.code;
    return bodyCode === UPSTREAM_UNAVAILABLE_CODE || error.headers?.get(UPSTREAM_UNAVAILABLE_HEADER) === UPSTREAM_UNAVAILABLE_CODE;
}
